import { get, type PracticeLog, type PracticeSession } from "./api.js";
import { dayKey } from "./format.js";
import { isPracticeHistorySummary, type PracticeHistoryDay } from "./PracticeHistory.js";
import { getStudentTutorPresentations, type TutorPresentation } from "./tutorProfileApi.js";
import { useAsync } from "./useAsync.js";

/**
 * Home's independent reads, each isolated so one slow source cannot hold up
 * the rest of the page. Kept in one module so tests can substitute them as a
 * unit instead of teaching a generic useAsync mock which call is which.
 */

export function useHomeTutors(studentId: string | null) {
  return useAsync(
    () => (studentId ? getStudentTutorPresentations(studentId) : Promise.resolve({ tutors: [] })),
    [studentId],
  );
}

export function useHomePracticeLog(studentId: string | null) {
  return useAsync(
    () =>
      studentId
        ? get<PracticeLog>(`/me/students/${encodeURIComponent(studentId)}/practice`)
        : Promise.resolve(null),
    [studentId],
  );
}

export function useHomePracticeHistory(studentId: string | null) {
  return useAsync(async () => {
    if (!studentId) return null;
    const summary = await get<unknown>(
      `/me/students/${encodeURIComponent(studentId)}/practice/history`,
    );
    return isPracticeHistorySummary(summary) ? summary : null;
  }, [studentId]);
}

/** Defensive readers: a partially-shaped response must degrade to "nothing", not crash Home. */
export function tutorsOf(data: unknown): TutorPresentation[] {
  const tutors = (data as { tutors?: unknown } | null)?.tutors;
  return Array.isArray(tutors) ? (tutors as TutorPresentation[]) : [];
}

export function sessionsOf(data: unknown): PracticeSession[] {
  const sessions = (data as { sessions?: unknown } | null)?.sessions;
  return Array.isArray(sessions) ? (sessions as PracticeSession[]) : [];
}

export function historyDaysOf(data: unknown): PracticeHistoryDay[] {
  const days = (data as { days?: unknown } | null)?.days;
  return Array.isArray(days) ? (days as PracticeHistoryDay[]) : [];
}

export interface WeeklyPracticeComparison {
  thisWeekSeconds: number;
  thisWeekDays: number;
  lastWeekSeconds: number;
  /** Positive when this week is ahead of last week. */
  deltaSeconds: number;
}

function shiftDateKey(dateKey: string, days: number): string {
  const date = new Date(`${dateKey}T12:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Monday at the start of the London calendar week containing this date. */
export function mondayOfDateKey(dateKey: string): string {
  const date = new Date(`${dateKey}T12:00:00.000Z`);
  const daysSinceMonday = (date.getUTCDay() + 6) % 7;
  return shiftDateKey(dateKey, -daysSinceMonday);
}

/**
 * This calendar week (Monday to today) against the whole of last week.
 *
 * The history endpoint already groups sessions by London calendar day, so the
 * comparison is a pure fold over those days; no session timestamps are
 * re-interpreted in the browser's own timezone.
 */
export function weeklyPracticeComparison(
  days: readonly PracticeHistoryDay[],
  today: string = dayKey(new Date().toISOString()),
): WeeklyPracticeComparison {
  const monday = mondayOfDateKey(today);
  const lastMonday = shiftDateKey(monday, -7);
  const lastSunday = shiftDateKey(monday, -1);

  let thisWeekSeconds = 0;
  let thisWeekDays = 0;
  let lastWeekSeconds = 0;
  for (const day of days) {
    if (day.date >= monday && day.date <= today) {
      thisWeekSeconds += day.totalSeconds;
      if (day.sessions > 0) thisWeekDays += 1;
    } else if (day.date >= lastMonday && day.date <= lastSunday) {
      lastWeekSeconds += day.totalSeconds;
    }
  }

  return {
    thisWeekSeconds,
    thisWeekDays,
    lastWeekSeconds,
    deltaSeconds: thisWeekSeconds - lastWeekSeconds,
  };
}
